// @ts-nocheck
// 💰 Συγκεντρωτικά πληρωμών καθαρισμού + export PDF/Word.
// Μοντέλο: χωρίς migration — χρησιμοποιεί cleaning_jobs.amount (χρέωση) + paid (boolean).
//   Χρέωση   = SUM(amount)
//   Πληρώθηκε = SUM(amount WHERE paid)
//   Υπόλοιπο  = SUM(amount WHERE NOT paid)
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { loadPdfFonts } from './loadPdfFonts';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, AlignmentType, HeadingLevel, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';

export interface VesselPayRow {
  vessel_name: string;
  count: number;
  charged: number;
  paid: number;
  balance: number;
  jobs: any[]; // για drill-down
}
export interface PaymentSummary {
  rows: VesselPayRow[];
  totals: { count: number; charged: number; paid: number; balance: number };
}

// Ομαδοποίηση ανά σκάφος (vessel_name)
export const buildPaymentSummary = (jobs: any[]): PaymentSummary => {
  const map: Record<string, VesselPayRow> = {};
  for (const j of jobs) {
    const name = j.vessel_name || '—';
    const amt = parseFloat(j.amount) || 0;
    const isPaid = !!j.paid;
    if (!map[name]) map[name] = { vessel_name: name, count: 0, charged: 0, paid: 0, balance: 0, jobs: [] };
    const row = map[name];
    row.count += 1;
    row.charged += amt;
    if (isPaid) row.paid += amt; else row.balance += amt;
    row.jobs.push(j);
  }
  const rows = Object.values(map).sort((a, b) => a.vessel_name.localeCompare(b.vessel_name, 'el'));
  const totals = rows.reduce((t, r) => ({
    count: t.count + r.count, charged: t.charged + r.charged, paid: t.paid + r.paid, balance: t.balance + r.balance,
  }), { count: 0, charged: 0, paid: 0, balance: 0 });
  return { rows, totals };
};

const money = (n: number) => `${(n || 0).toFixed(2)} €`;
const safeName = (s: string) => (s || '').replace(/[^0-9A-Za-zΑ-Ωα-ωΆ-Ώά-ώ]+/g, '-').replace(/^-+|-+$/g, '') || 'ολες';

// ---- PDF (ελληνικά via DejaVuSans) ----
export const exportPaymentsPDF = async (summary: PaymentSummary, periodTxt: string) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  await loadPdfFonts();
  if ((window as any).DejaVuSansBase64) {
    doc.addFileToVFS('DejaVuSans.ttf', (window as any).DejaVuSansBase64);
    doc.addFont('DejaVuSans.ttf', 'DejaVuSans', 'normal');
  }
  if ((window as any).DejaVuSansBoldBase64) {
    doc.addFileToVFS('DejaVuSans-Bold.ttf', (window as any).DejaVuSansBoldBase64);
    doc.addFont('DejaVuSans-Bold.ttf', 'DejaVuSans', 'bold');
  }
  doc.setFont('DejaVuSans', 'normal');
  const pageW = doc.internal.pageSize.getWidth();

  doc.setFontSize(16); doc.setFont('DejaVuSans', 'bold'); doc.setTextColor(30, 144, 255);
  doc.text('Πληρωμές Καθαρισμών', pageW / 2, 18, { align: 'center' });
  doc.setFontSize(10); doc.setFont('DejaVuSans', 'normal'); doc.setTextColor(55, 65, 81);
  doc.text(`Περίοδος: ${periodTxt}`, 14, 28);

  const body = summary.rows.map(r => [r.vessel_name, String(r.count), money(r.charged), money(r.paid), money(r.balance)]);
  body.push(['ΣΥΝΟΛΟ', String(summary.totals.count), money(summary.totals.charged), money(summary.totals.paid), money(summary.totals.balance)]);

  autoTable(doc, {
    startY: 34,
    head: [['Σκάφος', 'Καθαρισμοί', 'Χρέωση', 'Πληρώθηκε', 'Υπόλοιπο']],
    body,
    theme: 'striped',
    styles: { font: 'DejaVuSans', fontSize: 9 },
    headStyles: { fillColor: [30, 144, 255], textColor: 255, font: 'DejaVuSans', fontStyle: 'bold' },
    bodyStyles: { font: 'DejaVuSans', fontStyle: 'normal' },
    columnStyles: { 1: { halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
    didParseCell: (data: any) => {
      const last = data.row.index === body.length - 1;
      if (last) { data.cell.styles.fontStyle = 'bold'; data.cell.styles.fillColor = [224, 247, 255]; }
      if (data.section === 'body' && data.column.index === 4) {
        const v = String(data.cell.raw);
        const zero = v.startsWith('0.00');
        data.cell.styles.textColor = zero ? [21, 128, 61] : [185, 28, 28];
      }
    },
  });

  doc.save(`Πληρωμες-Καθαρισμων-${safeName(periodTxt)}.pdf`);
};

// ---- Word (docx) ----
const cell = (text: string, opts: any = {}) => new TableCell({
  width: opts.width ? { size: opts.width, type: WidthType.PERCENTAGE } : undefined,
  shading: opts.fill ? { fill: opts.fill } : undefined,
  children: [new Paragraph({
    alignment: opts.align || AlignmentType.LEFT,
    children: [new TextRun({ text, bold: !!opts.bold, color: opts.color, size: 20 })],
  })],
});

export const exportPaymentsWord = async (summary: PaymentSummary, periodTxt: string) => {
  const header = new TableRow({
    tableHeader: true,
    children: [
      cell('Σκάφος', { bold: true, color: 'FFFFFF', fill: '1E90FF', width: 32 }),
      cell('Καθαρισμοί', { bold: true, color: 'FFFFFF', fill: '1E90FF', align: AlignmentType.CENTER, width: 17 }),
      cell('Χρέωση', { bold: true, color: 'FFFFFF', fill: '1E90FF', align: AlignmentType.RIGHT, width: 17 }),
      cell('Πληρώθηκε', { bold: true, color: 'FFFFFF', fill: '1E90FF', align: AlignmentType.RIGHT, width: 17 }),
      cell('Υπόλοιπο', { bold: true, color: 'FFFFFF', fill: '1E90FF', align: AlignmentType.RIGHT, width: 17 }),
    ],
  });
  const rows = summary.rows.map(r => new TableRow({
    children: [
      cell(r.vessel_name),
      cell(String(r.count), { align: AlignmentType.CENTER }),
      cell(money(r.charged), { align: AlignmentType.RIGHT }),
      cell(money(r.paid), { align: AlignmentType.RIGHT }),
      cell(money(r.balance), { align: AlignmentType.RIGHT, color: (r.balance || 0) === 0 ? '157A3D' : 'B91C1C' }),
    ],
  }));
  const totalRow = new TableRow({
    children: [
      cell('ΣΥΝΟΛΟ', { bold: true, fill: 'E0F7FF' }),
      cell(String(summary.totals.count), { bold: true, align: AlignmentType.CENTER, fill: 'E0F7FF' }),
      cell(money(summary.totals.charged), { bold: true, align: AlignmentType.RIGHT, fill: 'E0F7FF' }),
      cell(money(summary.totals.paid), { bold: true, align: AlignmentType.RIGHT, fill: 'E0F7FF' }),
      cell(money(summary.totals.balance), { bold: true, align: AlignmentType.RIGHT, fill: 'E0F7FF' }),
    ],
  });

  const doc = new Document({
    sections: [{
      children: [
        new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: 'Πληρωμές Καθαρισμών', color: '1E90FF' })] }),
        new Paragraph({ children: [new TextRun({ text: `Περίοδος: ${periodTxt}`, size: 20 })] }),
        new Paragraph({ text: '' }),
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 4, color: 'CCCCCC' },
            bottom: { style: BorderStyle.SINGLE, size: 4, color: 'CCCCCC' },
            left: { style: BorderStyle.SINGLE, size: 4, color: 'CCCCCC' },
            right: { style: BorderStyle.SINGLE, size: 4, color: 'CCCCCC' },
            insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: 'DDDDDD' },
            insideVertical: { style: BorderStyle.SINGLE, size: 2, color: 'DDDDDD' },
          },
          rows: [header, ...rows, totalRow],
        }),
      ],
    }],
  });
  const blob = await Packer.toBlob(doc);
  saveAs(blob, `Πληρωμες-Καθαρισμων-${safeName(periodTxt)}.docx`);
};
